<script setup>
/**
 * CLICK WHAT YOU MEAN — the real answer from the owner's own system, with the three things
 * we need picked out of it by pointing.
 *
 * ── THE DEFECT THIS REPLACES ──────────────────────────────────────────────────────────
 * `TaskLiveMapping.vue` says it in its own docblock: *"Typing `slots[0].start_time` by
 * hand, from memory, into three boxes is where this feature failed in the partner's field
 * report."* `TaskKeepMap.vue` says the same thing about a `call`: two boxes per row, the
 * left one *"a dotted path into somebody else's JSON, which only she can know"*. Both are
 * asking a shop owner to remember the shape of an API.
 *
 * The answer is already on the screen. This draws it and lets her point at it.
 *
 * ── BUILT ON WHAT IS ALREADY THERE ────────────────────────────────────────────────────
 * `arrayPaths`, `fieldsAt`, `guessMapping`, `formatLiveLabel` and `readPath` are all in
 * `helpers/task-flow-draft.js` and all tested. Nothing about which path is a list, which
 * field is probably the label and how a raw value will read to a customer is re-decided
 * here — this is the same three answers, drawn as a tree instead of as three boxes.
 *
 * ONE THING IS LOCAL AND SAYS SO: finding the SCALARS in an answer, for a `call`'s "what
 * do we keep". `arrayPaths` finds arrays and nothing in that file finds leaves; adding one
 * there is new surface in a file four screens read, for one caller. It is the same walk
 * with the same depth cap, ten lines below.
 *
 * ── AND THE VALUE IS DRAWN HERE, WHICH IS NOT A CONTRADICTION ─────────────────────────
 * `TaskLiveMapping` says the value column is confirmed as a field NAME and never as a
 * value, because a value is an internal row id and the one thing a CUSTOMER must never be
 * shown. This is the owner's own dashboard showing the owner's own system's answer — the
 * rule is about the preview of the phone, which is a different block on a different screen
 * and still obeys it.
 */
import { computed } from 'vue';

import WasloInput from '../WasloInput.vue';
import { useWasloI18n } from '../../i18n.js';
import {
  arrayPaths,
  fieldsAt,
  formatLiveLabel,
  readPath,
} from '../../helpers/task-flow-draft.js';
import { deriveSaveKey } from '../../helpers/task-question.js';

const props = defineProps({
  /** The `data` of a real, successful "Try it", or `null` before one has run. */
  answer: { type: [Object, Array], default: null },
  /** `'live'` picks a list, a label and a value; `'keep'` picks what to remember. */
  mode: { type: String, default: 'live' },
  /** For `live`: the mapping so far. */
  mapping: {
    type: Object,
    default: () => ({
      listPath: '',
      labelField: '',
      valueField: '',
      format: 'text',
    }),
  },
  /** For `keep`: `{ [dotted path]: saveKey }`. */
  keep: { type: Object, default: () => ({}) },
  /** Save keys the rest of the task already writes, so a derived one cannot collide. */
  takenKeys: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:mapping', 'update:keep']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.RESPONSE.${name}`;

const MAX_SHOWN = 60;

/** One value as it really is, bounded. `dir="ltr"` at the call site: this is machine text. */
const shown = value => {
  if (value === null) return 'null';
  if (value === undefined) return '—';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > MAX_SHOWN ? `${text.slice(0, MAX_SHOWN)}…` : text;
};

/**
 * Every dotted path in the answer that holds a plain value. Same walk and same depth cap as
 * `arrayPaths`, which finds the other half of the same tree — see the header.
 */
const leafPaths = (value, prefix = '', depth = 0, out = []) => {
  if (
    depth > 4 ||
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value)
  ) {
    return out;
  }
  Object.entries(value).forEach(([name, child]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    if (child === null || typeof child !== 'object') {
      out.push(path);
      return;
    }
    if (!Array.isArray(child)) leafPaths(child, path, depth + 1, out);
  });
  return out;
};

const lists = computed(() => arrayPaths(props.answer));
/** How a list is named on screen: its path, or the owner's words for the whole answer (P-3.2). */
const listLabel = path => (path === '$' ? t(key('WHOLE_LIST')) : path);

const fields = computed(() =>
  props.mapping.listPath ? fieldsAt(props.answer, props.mapping.listPath) : []
);

const firstRow = computed(() => {
  const list = readPath(props.answer, props.mapping.listPath);
  return Array.isArray(list) && list.length ? list[0] : null;
});

const leaves = computed(() => leafPaths(props.answer));

const pickList = path =>
  emit('update:mapping', { ...props.mapping, listPath: path });

const pickField = (field, role) => {
  const next = { ...props.mapping };
  if (role === 'label') {
    next.labelField = next.labelField === field ? '' : field;
  } else {
    next.valueField = next.valueField === field ? '' : field;
  }
  emit('update:mapping', next);
};

const toggleKeep = path => {
  const next = { ...props.keep };
  if (path in next) delete next[path];
  else {
    next[path] = deriveSaveKey(path.split('.').pop() ?? path, [
      ...props.takenKeys,
      ...Object.values(next),
    ]);
  }
  emit('update:keep', next);
};

const renameKeep = (path, value) =>
  emit('update:keep', { ...props.keep, [path]: value });

/** What a label field's first value will actually read as on a phone. */
const asLabel = field =>
  formatLiveLabel(firstRow.value?.[field], props.mapping.format, locale.value);
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-response-picker>
    <p
      v-if="!answer"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-response-waiting
    >
      {{ t(key('WAITING')) }}
    </p>

    <template v-else-if="mode === 'live'">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </p>

      <p
        v-if="lists.length === 0"
        class="mb-0 text-sm text-n-amber-11"
        data-waslo-response-no-list
      >
        {{ t(key('NO_LIST')) }}
      </p>

      <ul v-else class="flex flex-col gap-1 mb-0" data-waslo-response-lists>
        <li v-for="path in lists" :key="path">
          <button
            type="button"
            class="flex items-center w-full gap-3 px-3 py-2 text-sm rounded-lg text-start"
            :class="
              mapping.listPath === path
                ? 'bg-n-blue-2 outline outline-1 outline-n-blue-8'
                : 'hover:bg-n-alpha-2'
            "
            :aria-pressed="mapping.listPath === path ? 'true' : 'false'"
            :data-waslo-response-list="path"
            @click="pickList(path)"
          >
            <span
              class="shrink-0 size-4 text-n-slate-11 i-lucide-list"
              aria-hidden="true"
            />
            <span
              class="flex-1 min-w-0 truncate text-n-slate-12"
              :class="path === '$' ? '' : 'font-mono'"
              :dir="path === '$' ? 'auto' : 'ltr'"
            >
              {{ listLabel(path) }}
            </span>
            <span
              v-if="mapping.listPath === path"
              class="text-n-blue-11"
              data-waslo-response-list-chosen
            >
              {{ t(key('IS_LIST')) }}
            </span>
          </button>
        </li>
      </ul>

      <div
        v-if="mapping.listPath && fields.length"
        class="flex flex-col gap-1"
        data-waslo-response-fields
      >
        <p class="mb-0 text-sm text-n-slate-11">{{ t(key('FIELDS')) }}</p>
        <div
          v-for="field in fields"
          :key="field"
          class="flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg bg-n-alpha-1"
          :data-waslo-response-field="field"
        >
          <span class="font-mono text-sm shrink-0 text-n-slate-12" dir="ltr">
            {{ field }}
          </span>
          <span
            class="flex-1 min-w-0 text-sm truncate text-n-slate-11"
            dir="ltr"
          >
            {{ shown(firstRow ? firstRow[field] : undefined) }}
          </span>
          <button
            type="button"
            class="px-2 py-1 text-sm rounded-md outline outline-1"
            :class="
              mapping.labelField === field
                ? 'outline-n-blue-8 bg-n-blue-2 text-n-blue-11'
                : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            :aria-pressed="mapping.labelField === field ? 'true' : 'false'"
            :data-waslo-response-label="field"
            @click="pickField(field, 'label')"
          >
            {{ t(key('THEY_READ')) }}
          </button>
          <button
            type="button"
            class="px-2 py-1 text-sm rounded-md outline outline-1"
            :class="
              mapping.valueField === field
                ? 'outline-n-blue-8 bg-n-blue-2 text-n-blue-11'
                : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            :aria-pressed="mapping.valueField === field ? 'true' : 'false'"
            :data-waslo-response-value="field"
            @click="pickField(field, 'value')"
          >
            {{ t(key('WE_KEEP')) }}
          </button>
        </div>
        <p
          v-if="mapping.labelField"
          class="mb-0 text-sm text-n-slate-11"
          data-waslo-response-reads-as
        >
          {{ t(key('READS_AS'), { text: asLabel(mapping.labelField) || '—' }) }}
        </p>
      </div>
    </template>

    <template v-else>
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('KEEP_HEADING')) }}
      </p>
      <p
        v-if="leaves.length === 0"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-response-no-fields
      >
        {{ t(key('NO_FIELDS')) }}
      </p>
      <div
        v-for="path in leaves"
        :key="path"
        class="flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg bg-n-alpha-1"
        :data-waslo-response-leaf="path"
      >
        <span class="font-mono text-sm shrink-0 text-n-slate-12" dir="ltr">
          {{ path }}
        </span>
        <span class="flex-1 min-w-0 text-sm truncate text-n-slate-11" dir="ltr">
          {{ shown(readPath(answer, path)) }}
        </span>
        <button
          type="button"
          class="px-2 py-1 text-sm rounded-md outline outline-1"
          :class="
            path in keep
              ? 'outline-n-blue-8 bg-n-blue-2 text-n-blue-11'
              : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          :aria-pressed="path in keep ? 'true' : 'false'"
          :data-waslo-response-keep="path"
          @click="toggleKeep(path)"
        >
          {{ t(key('WE_KEEP')) }}
        </button>
        <WasloInput
          v-if="path in keep"
          :model-value="keep[path]"
          :label="t(key('KEEP_AS'))"
          :data-waslo-response-keep-as="path"
          @update:model-value="value => renameKeep(path, value)"
        />
      </div>
    </template>
  </div>
</template>
